import type { PositionResponse } from "../../api/client";
import PnlValue from "../PnlValue";
import { cn } from "@/lib/utils";
import ProtectionStepper from "@/components/common/ProtectionStepper";
import {
  formatCloseSource,
  formatExitReason,
} from "../positions/closeLabels";

interface PositionLifecycleTableProps {
  /** All positions for the cycle (open + closed). Null while unfetched. */
  positions: PositionResponse[] | null;
  loading: boolean;
  error: boolean;
}

/**
 * Read-only table for the dashboard cockpit / history drawer that lists every
 * position attached to a cycle — open first (prominent), closed below.
 *
 * <p>Status distinguishes OPEN / CLOSED (bot-initiated) / RECONCILED (close
 * detected, synthetic price). Roles + protected state render the RAW backend
 * value verbatim (no INITIALE fallback / no relabel) so anomalies stay visible.
 * Realised P&L goes through {@link PnlValue} (unknown ≠ +€0.00).
 */
export default function PositionLifecycleTable({
  positions,
  loading,
  error,
}: PositionLifecycleTableProps) {
  if (error) {
    return (
      <p
        className="positions-panel__error text-loss p-4 text-sm"
        role="alert"
        data-testid="lifecycle-error"
      >
        Échec du chargement des positions. Cette vue n'est PAS l'état live — ne t'y fie
        pas tant que la connexion n'est pas rétablie.
      </p>
    );
  }
  if (loading && positions == null) {
    return <p className="text-muted-foreground p-4 text-sm">Chargement des positions…</p>;
  }
  if (positions == null || positions.length === 0) {
    return (
      <p className="text-muted-foreground p-4 text-sm">
        Aucune position dans ce cycle.
      </p>
    );
  }

  const open = positions.filter((p) => p.closedAt == null);
  const closed = positions.filter((p) => p.closedAt != null);
  const rows = [...open, ...closed];

  return (
    <div className="overflow-x-auto">
      <table
        className="lifecycle-table w-full border-collapse text-sm whitespace-nowrap"
        data-testid="lifecycle-table"
      >
        <thead>
          <tr className="text-muted-foreground border-border border-b text-left text-[0.68rem] tracking-wide uppercase">
            <th className="px-2 py-2 font-medium">Statut</th>
            <th className="px-2 py-2 font-medium">Sens</th>
            <th className="px-2 py-2 font-medium">Rôle</th>
            <th className="px-2 py-2 font-medium">Deal</th>
            <th className="px-2 py-2 font-medium">Entrée</th>
            <th className="px-2 py-2 font-medium">Sortie</th>
            <th className="px-2 py-2 font-medium">Stop</th>
            <th className="px-2 py-2 font-medium">Taille €/pt</th>
            <th className="px-2 py-2 font-medium">Protection</th>
            <th className="px-2 py-2 font-medium">Ouverte</th>
            <th className="px-2 py-2 font-medium">Fermée</th>
            <th className="px-2 py-2 font-medium">Durée</th>
            <th className="px-2 py-2 font-medium">Raison de sortie</th>
            <th className="px-2 py-2 font-medium">P&amp;L €</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const isOpen = p.closedAt == null;
            const isReconciled =
              p.closeSource != null && p.closeSource.startsWith("RECONCILE_");
            const synthetic = isReconciled;
            const statusLabel = isOpen
              ? "OPEN"
              : isReconciled
                ? "RECONCILED"
                : "CLOSED";
            const statusTone = isOpen
              ? "bg-buy-soft text-buy border-buy/30"
              : isReconciled
                ? "bg-warning-soft text-warning border-warning/30"
                : "bg-surface-2 text-muted-foreground border-border-strong";
            const exitLabel = formatExitReason(p.exitReason);
            const sourceLabel = formatCloseSource(p.closeSource);
            return (
              <tr
                key={p.dealId ?? p.id}
                className={cn(
                  "border-border/60 border-b font-mono tabular-nums",
                  isOpen ? undefined : "lifecycle-row--closed opacity-75",
                )}
                data-testid={`lifecycle-row-${p.id}`}
              >
                <td className="px-2 py-1.5">
                  <span
                    className={cn(
                      "inline-flex rounded border px-1.5 py-0.5 text-[0.65rem] font-semibold",
                      statusTone,
                    )}
                  >
                    {statusLabel}
                  </span>
                </td>
                <td
                  className={cn(
                    "dir px-2 py-1.5 font-semibold",
                    p.direction === "BUY"
                      ? "dir--buy text-buy"
                      : "dir--sell text-sell",
                  )}
                >
                  {p.direction}
                </td>
                <td className="text-foreground px-2 py-1.5 font-sans">{p.role}</td>
                <td className="px-2 py-1.5">
                  <code className="text-muted-foreground">
                    {p.dealId.slice(0, 8)}…
                  </code>
                </td>
                <td className="px-2 py-1.5">{Number(p.entryPrice).toFixed(2)}</td>
                <td className="px-2 py-1.5">
                  {p.closePrice == null ? "—" : Number(p.closePrice).toFixed(2)}
                </td>
                <td className="px-2 py-1.5">{Number(p.stopPrice).toFixed(2)}</td>
                <td className="px-2 py-1.5">
                  {Number(p.sizePerPointEur).toFixed(2)}
                </td>
                <td className="px-2 py-1.5">
                  <ProtectionStepper state={p.protectedState} />
                </td>
                <td className="text-muted-foreground px-2 py-1.5">
                  {formatTime(p.openedAt)}
                </td>
                <td className="text-muted-foreground px-2 py-1.5">
                  {p.closedAt ? formatTime(p.closedAt) : "—"}
                </td>
                <td className="text-muted-foreground px-2 py-1.5">
                  {formatDuration(p.openedAt, p.closedAt)}
                </td>
                <td
                  className="text-muted-foreground px-2 py-1.5 font-sans"
                  title={isOpen ? undefined : `Détection : ${sourceLabel}`}
                  data-testid={`lifecycle-exit-${p.id}`}
                  data-close-source={p.closeSource ?? ""}
                >
                  {isOpen ? "—" : exitLabel}
                </td>
                <td className="px-2 py-1.5">
                  <PnlValue
                    value={p.realizedPnlEur}
                    pnlComplete={p.realizedPnlEur == null ? null : true}
                    synthetic={synthetic}
                    testId={`lifecycle-pnl-${p.id}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString("en-GB", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDuration(startIso: string, endIso: string | null): string {
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return "—";
  const end = endIso ? Date.parse(endIso) : Date.now();
  if (Number.isNaN(end)) return "—";
  const secs = Math.max(0, Math.floor((end - start) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}
